import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDistanceToNow } from 'date-fns';

const AlertCard = ({ alert, onAcknowledge, onResolve }) => {
  const getUrgencyIcon = (urgency) => {
    const icons = {
      critical: 'AlertCircle',
      high: 'AlertTriangle',
      medium: 'Info',
      low: 'Bell'
    };
    return icons?.[urgency] || 'Bell';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      critical: 'text-destructive',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[urgency] || 'text-muted-foreground';
  };

  const getUrgencyBgColor = (urgency) => {
    const colors = {
      critical: 'bg-destructive/10 border-destructive',
      high: 'bg-warning/10 border-warning',
      medium: 'bg-primary/10 border-primary',
      low: 'bg-secondary border-border'
    };
    return colors?.[urgency] || 'bg-secondary border-border';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Active', color: 'bg-primary/10 text-primary' },
      acknowledged: { label: 'Acknowledged', color: 'bg-warning/10 text-warning' },
      assigned: { label: 'Assigned', color: 'bg-accent/10 text-accent' },
      resolved: { label: 'Resolved', color: 'bg-success/10 text-success' },
      escalated: { label: 'Escalated', color: 'bg-destructive/10 text-destructive' }
    };
    return badges?.[status] || badges?.active;
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      tco2e_deviation: 'tCO₂e Deviation',
      buffer_pool_alert: 'Buffer Pool',
      reversal_risk: 'Reversal Risk',
      leakage_detection: 'Leakage',
      compliance_violation: 'Compliance',
      threshold_breach: 'Threshold',
      data_quality_issue: 'Data Quality',
      verification_required: 'Verification'
    };
    return labels?.[type] || type;
  };

  const statusBadge = getStatusBadge(alert?.status);

  return (
    <div className={`bg-card border-2 rounded-lg p-5 transition-all hover:shadow-md ${getUrgencyBgColor(alert?.urgency)}`}>
      <div className="flex items-start gap-4">
        {/* Urgency Indicator */}
        <div className={`flex-shrink-0 p-3 rounded-lg ${getUrgencyBgColor(alert?.urgency)}`}>
          <Icon name={getUrgencyIcon(alert?.urgency)} size={24} className={getUrgencyColor(alert?.urgency)} />
        </div>

        {/* Alert Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-foreground">{alert?.title}</h3>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusBadge?.color}`}>
                  {statusBadge?.label}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="FolderTree" size={12} />
                  {alert?.projectName || 'Unknown Project'}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Tag" size={12} />
                  {getAlertTypeLabel(alert?.alertType)}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  {formatDistanceToNow(new Date(alert?.createdAt), { addSuffix: true })}
                </span>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-lg text-xs font-semibold uppercase ${getUrgencyBgColor(alert?.urgency)} ${getUrgencyColor(alert?.urgency)}`}>
              {alert?.urgency}
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-foreground mb-4">{alert?.description}</p>

          {/* Metrics */}
          {alert?.metricName && (
            <div className="bg-secondary/50 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Metric</p>
                  <p className="text-sm font-medium text-foreground">{alert?.metricName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                  <p className="text-sm font-semibold text-foreground">{alert?.metricValue?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Threshold</p>
                  <p className="text-sm font-semibold text-foreground">{alert?.thresholdValue?.toLocaleString()}</p>
                </div>
              </div>
              {alert?.triggeredCondition && (
                <div className="mt-2 pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground">Condition: <span className="text-foreground font-medium">{alert?.triggeredCondition}</span></p>
                </div>
              )}
            </div>
          )}

          {/* Agent Assignments */}
          {alert?.assignments?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">Assigned Agents:</p>
              <div className="flex flex-wrap gap-2">
                {alert?.assignments?.map(assignment => (
                  <div key={assignment?.id} className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-lg">
                    <Icon name="User" size={14} className="text-accent" />
                    <span className="text-xs font-medium text-foreground">{assignment?.agent?.name}</span>
                    <span className="text-xs text-muted-foreground">({assignment?.agent?.role})</span>
                    {assignment?.assignmentType === 'automatic' && (
                      <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">Auto</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {alert?.status === 'active' && (
              <Button
                onClick={() => onAcknowledge?.(alert?.id)}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                <Icon name="Check" size={14} className="mr-1" />
                Acknowledge
              </Button>
            )}
            {(alert?.status === 'acknowledged' || alert?.status === 'assigned') && (
              <Button
                onClick={() => onResolve?.(alert?.id)}
                variant="default"
                size="sm"
                className="text-xs"
              >
                <Icon name="CheckCircle" size={14} className="mr-1" />
                Resolve
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-xs">
              <Icon name="ExternalLink" size={14} className="mr-1" />
              View Details
            </Button>
          </div>

          {/* Escalation Timer */}
          {alert?.escalationTimer && alert?.status !== 'resolved' && (
            <div className="mt-3 flex items-center gap-2 text-xs text-warning">
              <Icon name="Clock" size={14} />
              <span>Escalation in {formatDistanceToNow(new Date(alert?.escalationTimer))}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertCard;